import { endpoints, baseurl } from "../Utils/Constants";

// Sends one Stedi request and returns the parsed body; throws with the API message (and the
// 409 patient candidates, when the backend can't tell which existing patient is meant).
// A FormData body goes as multipart, with the browser setting its Content-Type; anything else as JSON.
const stediRequest = async (path, { method = "GET", token, body, fallbackMessage }) => {
    const isForm = body instanceof FormData;
    const response = await fetch(baseurl + path, {
        method,
        headers: {
            ...(isForm ? {} : { "Content-Type": "application/json" }),
            "ngrok-skip-browser-warning": "true",
            "Authorization": `Bearer ${token}`,
        },
        body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
    const responseText = await response.text();
    let result = null;
    if (responseText) {
        try {
            result = JSON.parse(responseText);
        } catch {
            result = responseText;
        }
    }

    if (!response.ok || result?.is_success === false) {
        const apiMessage = typeof result === "object" && result !== null
            ? result.message || result.detail || result.error
            : result;
        const readableApiMessage = typeof apiMessage === "string" ? apiMessage : apiMessage ? JSON.stringify(apiMessage) : "";
        const candidates = result?.data?.candidates;
        const candidateText = Array.isArray(candidates) && candidates.length
            ? ` Matching patients: ${candidates.map((candidate) => `${candidate.name} (${candidate.dob})`).join(", ")}.`
            : "";
        const status = result?.status_code ?? response.status;
        const err = new Error(readableApiMessage
            ? `${readableApiMessage}${candidateText} (HTTP ${status})`
            : `${fallbackMessage} (HTTP ${status}).`);
        err.data = result?.data;
        err.status = status;
        throw err;
    }
    return result;
};

const withOrganization = (path, organizationId, extra = {}) => {
    if (!organizationId) {
        throw new Error("Select an organization first.");
    }
    return `${path}?${new URLSearchParams({ organizationId: String(organizationId), ...extra })}`;
};

// The organization's providers that have an NPI: the provider options of the eligibility / claim status forms.
export const getOrganizationProviders = (organizationId, token) => stediRequest(
    withOrganization(endpoints.GET_ORGANIZATION_PROVIDERS, organizationId),
    { token, fallbackMessage: "Providers request failed" },
);

export const getInsuranceList = (token) => stediRequest(endpoints.GET_INSURANCE_LIST, {
    token,
    fallbackMessage: "Insurance list request failed",
});

export const getAllPayers = (token) => stediRequest(endpoints.GET_ALL_PAYERS, {
    token,
    fallbackMessage: "Payers request failed",
});

export const getEligibilityVerification = (id, token) => stediRequest(
    endpoints.GET_ELIGIBILITY_LIST.replace("{verification_id}", encodeURIComponent(id)),
    { token, fallbackMessage: "Eligibility verification request failed" },
);

// Bulk checks: the uploaded sheet plus the form's defaults as multipart; rows run in the background
// and the sheet comes back by email with the results added.
export const postBulkEligibility = (formData, organizationId, token) => stediRequest(
    withOrganization(endpoints.POST_BULK_ELIGIBILITY, organizationId),
    { method: "POST", token, body: formData, fallbackMessage: "Bulk eligibility request failed" },
);

export const postBulkClaimStatus = (formData, organizationId, token) => stediRequest(
    withOrganization(endpoints.POST_BULK_CLAIM_STATUS, organizationId),
    { method: "POST", token, body: formData, fallbackMessage: "Bulk claim status request failed" },
);

// Saves the styled .xlsx upload template ("eligibility" or "claim-status"), built by the API with the
// Insurance Name dropdown filled from the Stedi payer list.
export const downloadBulkTemplate = async (kind, token) => {
    const response = await fetch(`${baseurl}${endpoints.GET_BULK_TEMPLATE}${kind}`, {
        headers: { "ngrok-skip-browser-warning": "true", "Authorization": `Bearer ${token}` },
    });
    const type = response.headers.get("Content-Type") || "";
    if (!response.ok || !type.includes("spreadsheetml")) {
        throw new Error("Could not download the template.");
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = `${kind}-bulk-template.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
};

// Pulls Stedi results for the caller's unfinished batch checks into the eligibility list.
export const refreshEligibilityBatches = (token) => stediRequest(endpoints.POST_REFRESH_BATCH_ELIGIBILITY, {
    method: "POST",
    token,
    fallbackMessage: "Batch eligibility refresh failed",
});

// Saves a Claims 360 list ("eligibility", "claim-status" or "denials") as .xlsx, in the same format as the
// emailed bulk report. ids limits it to the rows the list shows; the API scopes it to the caller's own checks.
export const exportClaims360List = async (kind, ids, token) => {
    const response = await fetch(baseurl + endpoints.EXPORT_CLAIMS360[kind], {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
            "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
    });
    const type = response.headers.get("Content-Type") || "";
    if (!response.ok || !type.includes("spreadsheetml")) {
        let message = "";
        try {
            message = (await response.json())?.message || "";
        } catch {
            // Not JSON; fall back to the generic message.
        }
        throw new Error(message || `Could not export the list (HTTP ${response.status}).`);
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    const filename = disposition.match(/filename="?([^";]+)"?/)?.[1]
        || `${kind}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

// The API sends the organization's name and the provider's NPI as the billing provider.
export const postClaimStatus = (data, organizationId, providerId, token) => stediRequest(
    withOrganization(endpoints.POST_CLAIM_STATUS, organizationId, { providerId: String(providerId) }),
    { method: "POST", token, body: data, fallbackMessage: "Claim status request failed" },
);

// Claims payers finalized as denied, for Denial Management.
export const getDeniedClaims = (token) => stediRequest(endpoints.GET_DENIED_CLAIMS, {
    token,
    fallbackMessage: "Denied claims request failed",
});

export const getClaimStatusRecord = (id, token) => stediRequest(
    endpoints.GET_CLAIMSTATUS_LIST.replace("{record_id}", encodeURIComponent(id)),
    { token, fallbackMessage: "Claim status request failed" },
);

export const getEligibilitylist = async (token) => {
    try {
        const response = await fetch(baseurl + endpoints.GET_ELIGIBILIT, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                "Authorization": `Bearer ${token}`,
            },
        });
        const responseText = await response.text();
        let result = null;

        if (responseText) {
            try {
                result = JSON.parse(responseText);
            } catch {
                result = responseText;
            }
        }

        if (!response.ok || result?.is_success === false) {
            const apiMessage = typeof result === "object" && result !== null
                ? result.message || result.detail || result.error
                : result;
            const message = apiMessage
                ? `${String(apiMessage)} (HTTP ${response.status} ${response.statusText})`
                : `Eligibility request failed (HTTP ${response.status} ${response.statusText}).`;

            throw new Error(message);
        }

        return result;
    } catch (error) {
        console.error("Error fetching eligibility records:", error);
        throw error;
    }
};


// The API sends the organization's name and the provider's NPI as the requesting provider.
export const postEligibility = (data, organizationId, providerId, token) => stediRequest(
    withOrganization(endpoints.POST_ELIGIBILITY, organizationId, { providerId: String(providerId) }),
    { method: "POST", token, body: data, fallbackMessage: "Eligibility request failed" },
);



export const uploadExcelEligibility = async (data, token) => { 
     try {
        const response = await fetch(baseurl + endpoints.UPLOAD_EXCEL_ELIGIBILITY, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(data)
        });
        const responseText = await response.text();
        let result = null;

        if (responseText) {
            try {
                result = JSON.parse(responseText);
            } catch {
                result = responseText;
            }
        }

        if (!response.ok || result?.is_success === false) {
            const apiMessage = typeof result === "object" && result !== null
                ? result.message || result.detail || result.error
                : result;
            const readableApiMessage = typeof apiMessage === "string"
                ? apiMessage
                : apiMessage
                    ? JSON.stringify(apiMessage)
                    : "";
            const message = readableApiMessage
                ? `${readableApiMessage} (HTTP ${response.status} ${response.statusText})`
                : `Eligibility request failed (HTTP ${response.status} ${response.statusText}).`;
            throw new Error(message);
        }

        return result;
    } catch (error) {
        console.error("Error saving eligibility verification:", error);
        throw error;
    }
}






export const claimStatusList = async (token) => {
    try {
        const response = await fetch(baseurl + endpoints.GET_CLAIMSTATUS, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                "Authorization": `Bearer ${token}`,
            },
        });
        const responseText = await response.text();
        let result = null;

        if (responseText) {
            try {
                result = JSON.parse(responseText);
            } catch {
                result = responseText;
            }
        }

        if (!response.ok || result?.is_success === false) {
            const apiMessage = typeof result === "object" && result !== null
                ? result.message || result.detail || result.error
                : result;
            const message = apiMessage
                ? `${String(apiMessage)} (HTTP ${response.status} ${response.statusText})`
                : `Claim status request failed (HTTP ${response.status} ${response.statusText}).`;

            throw new Error(message);
        }

        return result;
    } catch (error) {
        console.error("Error fetching claim statuses:", error);
        throw error;
    }
};



export const getClaimSubmission = async (token, organizationId) => {
   try {
   if (!organizationId) {
       throw new Error("An organization ID is required to load claim submissions.");
   }

   const query = new URLSearchParams({ organizationId: String(organizationId) });
   const response = await fetch(`${baseurl}${endpoints.GET_CLAIMSUBISSION}?${query}`, {
     method: "GET",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                "Authorization": `Bearer ${token}`,
            },
            });
            const responseText = await response.text();
            let result = null;

            if (responseText) {
                try {
                result = JSON.parse(responseText);
                } catch {
                result = responseText;
                }
            }

            if (!response.ok || result?.is_success === false) {
                const apiMessage = typeof result === "object" && result !== null
                ? result.message || result.detail || result.error
                : result;
                const readableApiMessage = typeof apiMessage === "string"
                    ? apiMessage
                    : apiMessage
                        ? JSON.stringify(apiMessage)
                        : "";
                const message = apiMessage
                ? `${readableApiMessage} (HTTP ${response.status} ${response.statusText})`
                : `Claim submission request failed (HTTP ${response.status} ${response.statusText}).`;
                throw new Error(message);
            }

            return result;
            } catch (error) {
            console.error("Error fetching claim submissions:", error);
            throw error;
            }
            }
